import React from 'react';
import { Box, Group, Popover, Text, UnstyledButton } from '@mantine/core';
import { IconCalendar, IconChevronDown } from '@tabler/icons';
import DateFilter from '../filters/types/DateFilter';
import { useFiltersContextHelper } from '../filters/useFiltersContextHelper';
import { useIdeaFilters } from '../../../hooks/useIdeaFilters';
import { useIdeasUrlState } from '../../../hooks/useIdeasUrlState';

const PRESET_OPTIONS = [
  { key: 'All', label: 'All time', days: 0 },
  { key: '12m', label: 'Last 12 months', days: 365 },
  { key: '3m', label: 'Last 3 months', days: 90 },
  { key: '1m', label: 'Last month', days: 30 },
  { key: '1w', label: 'Last week', days: 7 },
] as const;

const CreatedAtFiltersIdeas: React.FC = ({ subPageKey }: any) => {
  const [opened, setOpened] = React.useState(false);
  const [showCalendar, setShowCalendar] = React.useState(false);
  const [selection, setSelection] = React.useState<string>('All');
  const {
    setRangeDates,
    setOperator,
    setSelectedParentFilter,
  } = useFiltersContextHelper();
  const { ideasFiltersConfig } = useIdeaFilters();
  const { globalFilters, setDateRange } = useIdeasUrlState({ subPageKey });

  const createdAtKey = React.useMemo(() => {
    const cfgAny: any = ideasFiltersConfig as any;
    const cfg = (cfgAny?.evidence) || (cfgAny?.data?.evidence) || {};
    const keys: string[] = Object.keys(cfg || {});
    const preferred = keys.find((k) => {
      const f = cfg[k];
      const label = (f?.label || k || '').toString().toLowerCase();
      return f?.type === 'date' && (label.includes('created'));
    });
    if (preferred) return preferred;
    const firstDate = keys.find((k) => cfg[k]?.type === 'date');
    return firstDate || 'createdAt';
  }, [ideasFiltersConfig]);

  const startOfDay = React.useCallback((d: Date) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  }, []);

  const endOfDay = React.useCallback((d: Date) => {
    const x = new Date(d);
    x.setHours(23, 59, 59, 999);
    return x;
  }, []);

  const getLastNDaysRange = React.useCallback((days: number): [Date, Date] => {
    const end = endOfDay(new Date());
    const start = startOfDay(new Date(end));
    start.setDate(start.getDate() - (days - 1));
    return [start, end];
  }, [startOfDay, endOfDay]);

  React.useEffect(() => {
    if (!globalFilters?.dateRange) {
      setSelection('All');
      return;
    }

    const { start, end } = globalFilters.dateRange;
    if (!start || !end) {
      setSelection('All');
      return;
    }

    const rangeStart = new Date(start);
    const rangeEnd = new Date(end);
    const normRangeStart = startOfDay(rangeStart).getTime();
    const normRangeEnd = endOfDay(rangeEnd).getTime();

    const presets: { label: string; days: number }[] = [
      { label: '12m', days: 365 },
      { label: '3m', days: 90 },
      { label: '1m', days: 30 },
      { label: '1w', days: 7 },
    ];

    for (const preset of presets) {
      const [presetStart, presetEnd] = getLastNDaysRange(preset.days);
      if (normRangeStart === presetStart.getTime() && normRangeEnd === presetEnd.getTime()) {
        setSelection(preset.label);
        return;
      }
    }

    setSelection('calendar');
  }, [globalFilters?.dateRange, startOfDay, endOfDay, getLastNDaysRange]);

  const handlePresetSelect = React.useCallback(
    (preset: typeof PRESET_OPTIONS[number]) => {
      if (preset.key === 'All') {
        setSelection('All');
        setSelectedParentFilter(null);
        setRangeDates((prev: any) => ({
          ...(prev || {}),
          [createdAtKey]: [null, null],
        }));
        setOperator((prev: any) => ({
          ...(prev || {}),
          [createdAtKey]: undefined,
        }));
        setDateRange(null);
      } else {
        const [start, end] = getLastNDaysRange(preset.days);
        setSelectedParentFilter(createdAtKey);
        setRangeDates((prev: any) => ({
          ...(prev || {}),
          [createdAtKey]: [start, end],
        }));
        setOperator((prev: any) => ({
          ...(prev || {}),
          [createdAtKey]: 'custom',
        }));
        setDateRange({ start: start.toISOString(), end: end.toISOString() });
        setSelection(preset.key);
      }
      setOpened(false);
      setShowCalendar(false);
    },
    [createdAtKey, getLastNDaysRange, setDateRange, setOperator, setRangeDates, setSelectedParentFilter]
  );

  const displayText = React.useMemo(() => {
    if (selection === 'calendar') return 'Custom range';
    const preset = PRESET_OPTIONS.find((p) => p.key === selection);
    return preset?.label || 'All time';
  }, [selection]);

  const trigger = (
    <UnstyledButton
      aria-label="Date filter"
      onClick={() => setOpened((o) => !o)}
      style={{
        height: 32,
        boxSizing: 'border-box',
        border: '1px solid #E5E7EB',
        borderRadius: 8,
        padding: '0 8px',
        background: '#FFFFFF',
        width: 145,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: 'pointer',
      }}
    >
      <Group spacing={6} noWrap h={32}>
        <IconCalendar size={18} color="#6B7280" />
        <Text fz={12} lh={1.2}>
          {displayText}
        </Text>
      </Group>
      <IconChevronDown size={14} color="#6B7280" />
    </UnstyledButton>
  );

  return (
    <Popover
      opened={opened}
      onChange={(isOpen) => {
        setOpened(isOpen);
        if (!isOpen) setShowCalendar(false);
      }}
      position="bottom-start"
      radius="md"
      shadow="md"
      width={showCalendar ? undefined : 200}
    >
      <Popover.Target>{trigger}</Popover.Target>
      <Popover.Dropdown sx={{ padding: 0 }}>
        {!showCalendar ? (
          <Box py={4}>
            {PRESET_OPTIONS.map((preset) => (
              <UnstyledButton
                key={preset.key}
                onClick={() => handlePresetSelect(preset)}
                sx={{
                  display: 'block',
                  width: '100%',
                  padding: '7px 12px',
                  fontSize: 12,
                  background: selection === preset.key ? '#EEF0FF' : 'transparent',
                  '&:hover': {
                    background: selection === preset.key ? '#E6E9FF' : '#F8F9FA',
                  },
                }}
              >
                <Text fz={12} fw={500}>
                  {preset.label}
                </Text>
              </UnstyledButton>
            ))}
            <Box sx={{ borderTop: '1px solid #E5E7EB' }} my={4} />
            <UnstyledButton
              onClick={() => {
                setSelectedParentFilter(createdAtKey);
                setShowCalendar(true);
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                width: '100%',
                padding: '7px 12px',
                fontSize: 12,
                background: selection === 'calendar' ? '#EEF0FF' : 'transparent',
                '&:hover': {
                  background: selection === 'calendar' ? '#E6E9FF' : '#F8F9FA',
                },
              }}
            >
              <IconCalendar size={14} />
              <Text fz={12} fw={500}>
                Custom date range
              </Text>
            </UnstyledButton>
          </Box>
        ) : (
          <Box p={8}>
            <DateFilter
              MenuType="evidence"
              hideOperations
              onApplied={() => {
                setSelection('calendar');
                setOpened(false);
                setShowCalendar(false);
              }}
              setValue={setDateRange}
            />
          </Box>
        )}
      </Popover.Dropdown>
    </Popover>
  );
};

export default CreatedAtFiltersIdeas;
